import {
  detailHrefFor,
  INTEGRATION_CATALOG,
  INTEGRATION_CATEGORY_ORDER,
  toIntegrationConnection,
  type IntegrationCategoryKey,
} from '@/components/integrations/integration-catalog';
import IntegrationTypeSelector from '@/components/integrations/IntegrationTypeSelector';
import { PageHeader } from '@/components/ui';
import { useIntegrationsQuery } from '@/hooks/queries/use-integrations-query';
import { usePageTitleStore } from '@/stores/page-title';
import { Box, Center, Separator, Spinner, Stack, Text } from '@chakra-ui/react';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const IntegrationsPage = () => {
  const { t } = useTranslation();
  const setTitle = usePageTitleStore((state) => state.setTitle);

  const categoryLabel = (key: IntegrationCategoryKey) => t(`settings.integrationsPage.${key}`);
  const allLabel = t('settings.integrationsPage.all');
  const [selectedType, setSelectedType] = useState<string>(allLabel);

  useEffect(() => {
    setTitle(t('settings.integrations'));
    return () => setTitle('');
  }, [setTitle, t]);

  const { data: fetchedIntegrations, isLoading, isError } = useIntegrationsQuery();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Center h="100vh">
        <Text color="red.500">{t('settings.integrationsPage.errorLoading')}</Text>
      </Center>
    );
  }

  const categories = INTEGRATION_CATEGORY_ORDER.map((key) => ({
    key,
    label: categoryLabel(key),
    items: INTEGRATION_CATALOG.filter((entry) => entry.category === key).map((entry) => ({
      ...entry,
      ...toIntegrationConnection(fetchedIntegrations?.find((i) => i.platform === entry.platform)),
    })),
  })).filter((category) => category.items.length > 0);

  const visibleCategories =
    selectedType === allLabel
      ? categories
      : categories.filter((category) => category.label === selectedType);

  const integrationTypes = [allLabel, ...categories.map((category) => category.label)];

  return (
    <Box maxW="4xl" mx="auto">
      <PageHeader
        title={t('settings.integrations')}
        titleSize="xl"
        marginBottom={4}
        description={t('settings.integrationsPage.description')}
        actions={
          <IntegrationTypeSelector
            defaultType={allLabel}
            integrationTypes={integrationTypes}
            onTypeChange={(newType) => setSelectedType(newType)}
          />
        }
      />

      <Stack gap={6} mt={4}>
        {visibleCategories.map((category) => (
          <Box key={category.key}>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textTransform="uppercase"
              letterSpacing="wide"
              color="fg.muted"
              mb={2}
            >
              {category.label}
            </Text>
            <Box
              borderWidth="1px"
              borderColor="border"
              rounded="xl"
              overflow="hidden"
              bg="bg.panel"
            >
              {category.items.map(
                (
                  { platform, Component, id, isConnected, needsRefresh, senderEmail, metadata },
                  idx
                ) => (
                  <Fragment key={platform}>
                    {idx > 0 && <Separator borderColor="border" />}
                    <Component
                      id={id}
                      isConnected={isConnected}
                      needsRefresh={needsRefresh}
                      senderEmail={senderEmail}
                      metadata={metadata}
                      variant="row"
                      detailHref={detailHrefFor(platform)}
                    />
                  </Fragment>
                )
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default IntegrationsPage;
